import { BackLink, Dropdown, Field, Intro } from "components/commons";
import RefreshIcon from "images/assets/refresh-icon.svg";
import { getInvoiceGuides, getSingleInvoiceGuideById, refreshAllInvoiceGuides } from "apis";
import { Path } from "enums";
import { useApi, useModal } from "hooks";
import React, { useCallback, useMemo, useState } from "react";
import styles from "./single-invoicing-history.module.scss";
import DataTableV2 from "components/commons/data-table/data-table-v2";
import useDataTable from "hooks/useDataTable";
import SingleInvoicingGuideModal from "./single-invoicing-guide.modal";
import {
  apiMapper,
  mapDataToList,
  mapFilterToRequest,
  mapInvoiceGuideView,
  singleInvoicingHistoryColumns,
} from "./single-invoicing-history.mapper";

const MONTH_OPTIONS = [
  { label: "January", value: "01" },
  { label: "February", value: "02" },
  { label: "March", value: "03" },
  { label: "April", value: "04" },
  { label: "May", value: "05" },
  { label: "June", value: "06" },
  { label: "July", value: "07" },
  { label: "August", value: "08" },
  { label: "September", value: "09" },
  { label: "October", value: "10" },
  { label: "November", value: "11" },
  { label: "December", value: "12" },
];

const PLBSingleInvoicingHistoryModule = () => {
  const [invoiceGuideView, setInvoiceGuideView] = useState({});
  const [viewingInvoiceId, setViewingInvoiceId] = useState(null);
  const invoiceGuideModal = useModal();

  const now = new Date();
  const defaultInvoiceMonth = `${now.getMonth() + 1}`.padStart(2, "0");
  const defaultInvoiceYear = `${Math.max(2026, now.getFullYear())}`;

  const normalizeInvoiceGuidesResponse = useCallback(async (params = {}) => {
    const response = await getInvoiceGuides(params);

    const rawList = Array.isArray(response)
      ? response
      : response?.invoiceGuides || response?.singleInvoiceGuides || response?.data || response?.items || [];

    return {
      invoiceGuides: rawList,
      count: Array.isArray(response) ? rawList.length : response?.count || rawList.length,
    };
  }, []);

  const { request: getInvoiceGuideByIdRequest } = useApi({
    api: getSingleInvoiceGuideById,
    handleOwnError: true,
  });

  const onViewInvoiceGuide = useCallback(async (invoiceGuide = {}) => {
    try {
      const singleInvoiceId = invoiceGuide?.singleInvoiceId;
      if (!singleInvoiceId) {
        return;
      }

      setViewingInvoiceId(singleInvoiceId);

      const invoiceGuideResponse = await getInvoiceGuideByIdRequest({
        singleInvoiceId,
      });

      setInvoiceGuideView(mapInvoiceGuideView(invoiceGuideResponse, invoiceGuide));
      invoiceGuideModal.show({});
    } catch (error) {
    } finally {
      setViewingInvoiceId(null);
    }
  }, [getInvoiceGuideByIdRequest, invoiceGuideModal]);

  const { filter, table } = useDataTable({
    api: {
      api: normalizeInvoiceGuidesResponse,
      mapper: apiMapper,
    },
    filter: {
      initialState: {
        invoiceMonth: defaultInvoiceMonth,
        invoiceYear: defaultInvoiceYear,
      },
      mapper: mapFilterToRequest,
    },
    table: {
      key: "invoiceGuides",
      mapper: (invoiceGuide) => mapDataToList(invoiceGuide, onViewInvoiceGuide, viewingInvoiceId),
      columns: singleInvoicingHistoryColumns,
    },
  });

  const yearOptions = useMemo(() => {
    const startYear = 2026;
    const currentYear = new Date().getFullYear();
    const years = [];

    for (let year = currentYear; year >= startYear; year--) {
      years.push({ label: `${year}`, value: `${year}` });
    }

    return years;
  }, []);

  const applyPeriodFilter = useCallback(
    (name, value) => {
      const nextFilterState = filter.modifyFilter({ [name]: value });
      table.fetch({
        newPageState: {
          page: 1,
          perPage: table.pageState?.perPage || 10,
          isReverse: false,
        },
        newFilterState: mapFilterToRequest(nextFilterState),
      });
    },
    [filter, table]
  );

  return (
    <div>
      <SingleInvoicingGuideModal
        invoiceGuideModal={invoiceGuideModal}
        invoiceGuideView={invoiceGuideView}
      />

      <BackLink text="Create Single Invoice Guide" path={Path.PLBSingleInvoicing} />

      <Intro
        title="Invoice History"
        subtitle="View previous invoice guides"
        actionText="Refresh"
        actionOutlined
        actionOnClick={async () => {
          await refreshAllInvoiceGuides();
          table.refetch();
        }}
        actionStartIcon={<img src={RefreshIcon} alt="refresh" />}
      />

      <div className={styles.filters}>
        <Field className={styles.field} label="Invoice Period">
          <div className={styles.periodFilters}>
            <Dropdown
              name="invoiceMonth"
              className={styles.filterDropdown}
              value={filter.filterState?.invoiceMonth}
              onChange={(value) => {
                applyPeriodFilter("invoiceMonth", value);
              }}
              options={MONTH_OPTIONS}
            />
            <Dropdown
              name="invoiceYear"
              className={styles.filterDropdown}
              value={filter.filterState?.invoiceYear}
              onChange={(value) => {
                applyPeriodFilter("invoiceYear", value);
              }}
              options={yearOptions}
            />
          </div>
        </Field>
      </div>

      <div className={styles.tableContainer}>
        <DataTableV2 {...table} paginationV2={true} />
      </div>

    </div>
  );
};

export default PLBSingleInvoicingHistoryModule;



